import Close from '@mui/icons-material/Close';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AddOnRateType, SettlementLineItemType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { AddOn, AddOnCharge } from '~hooks/useAddOns/models';
import { Settlement } from '~hooks/useSettlements/models';
import { rateTypeTextByAddOnRateType } from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import { useStores } from '~store';
import { usePrevious } from '~utils/hooks/usePrevious';
import { formatPercentage } from '~utils/utilFunctions';

import { AddOnChargesDTO, AddOnChargesFormHandler } from '../../AddOnChargesTableRows';
import { appendAddOnRateTypeToName, invoiceHelpers, schemaHelpers } from '../../helpers';
import { GridConfigType } from './constants';

type AddOnSelectOption = {
  id: string;
  name: string;
};
type SettlementAddOnChargesProps = {
  settlementId: string;
  settlementAddOnCharges: AddOnCharge[];
  currencyFormatter: (value: number) => string;
  isEditing: boolean;
  gridConfig: GridConfigType;
  isRebill?: boolean;
  settlement?: Settlement;
};

const SettlementAddOnCharges = forwardRef<
  AddOnChargesFormHandler,
  SettlementAddOnChargesProps
>(function SettlementAddOnCharges(
  {
    settlementId,
    settlementAddOnCharges,
    currencyFormatter,
    isEditing,
    gridConfig,
    isRebill = false,
    settlement,
  },
  ref,
) {
  const theme = useTheme();
  const { settlementsStore, addOnsStore } = useStores();

  // When ticket numbers column is present (LOAD type), grid has 14 columns; otherwise 13
  // (Added 1 for the new "Type" column between Qty and Rate Type)
  const hasTicketNumbersColumn =
    settlement?.settlementLineItemType === SettlementLineItemType.LOAD;
  // Job ID column: column 6 when ticket numbers present, column 6 otherwise (no change needed)
  // Rate type column: column 10 when ticket numbers present, column 9 when not
  // (Shifted by 1 due to new "Type" column)
  const rateTypeColumn = hasTicketNumbersColumn ? '10 / 11' : '8 / 9';

  const {
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
  } = useFormContext<AddOnChargesDTO>();

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'addOnCharges',
  });

  const watchedAddOnCharges = useWatch({ control, name: 'addOnCharges' });
  const previousWatchedAddOnCharges = usePrevious(watchedAddOnCharges);

  const handleAppendAddOn = useCallback(() => {
    const addOnCharge = new AddOnCharge(
      null,
      '',
      0,
      0,
      0,
      0,
      AddOnRateType.RATE_FOR_EACH,
      null,
      { id: '' },
      null,
      isRebill ? 0 : null,
      isRebill ? 0 : null,
      isRebill ? 0 : null,
      null,
    );

    append(schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge));
  }, [append]);

  const addOnSelectOptions = useMemo(() => {
    const options = addOnsStore.addOns.reduce((options, addOn) => {
      options.push({
        id: String(addOn.id),
        name: appendAddOnRateTypeToName(addOn.name, addOn.revenueCostRateType),
      });

      return options;
    }, [] as AddOnSelectOption[]);

    return options;
  }, [settlementId, addOnsStore.addOns]);

  const handleRemoveAddOnClick = useCallback(
    (index: number) => {
      const addOnChargeToDelete = watchedAddOnCharges?.[index];

      if (addOnChargeToDelete?.id) {
        update(index, { ...addOnChargeToDelete, _destroy: 1 });
      } else {
        remove(index);
      }
    },
    [update, watchedAddOnCharges],
  );

  const addOnByAddOnId = useMemo(() => {
    const addOnByAddOnId = addOnsStore.addOns.reduce(
      (acc, addOn) => {
        acc[addOn.id ?? ''] = addOn;
        return acc;
      },
      {} as Record<string, AddOn>,
    );
    return addOnByAddOnId;
  }, [settlementId, addOnsStore.addOns, settlementsStore.setAddOnChargesBySettlementId]);

  const originalAddOnChargesById = useMemo(
    () => new Map(settlementAddOnCharges.map((charge) => [charge.id, charge])),
    [settlementAddOnCharges],
  );

  useEffect(() => {
    if (!watchedAddOnCharges || !previousWatchedAddOnCharges) {
      return;
    }

    watchedAddOnCharges.forEach((current, index) => {
      const currentAddOnId = current.addOn?.id;
      const previousAddOnIn = previousWatchedAddOnCharges[index]?.addOn?.id;
      const selectedAddOnDidChange = currentAddOnId && currentAddOnId !== previousAddOnIn;
      // If the selected add-on did change then use the newly selected add-on values
      // As the default values for the add-on charge DTO
      if (selectedAddOnDidChange && addOnByAddOnId[currentAddOnId]) {
        const newlySelectedAddOn = addOnByAddOnId[currentAddOnId];
        const isNonRatePercentOfTotal =
          newlySelectedAddOn.revenueCostRateType === AddOnRateType.RATE_FOR_EACH ||
          newlySelectedAddOn.revenueCostRateType === AddOnRateType.PER_LOAD ||
          (newlySelectedAddOn.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
            !newlySelectedAddOn.percentage);
        const addOnCharge = new AddOnCharge(
          current.id,
          newlySelectedAddOn.name,
          newlySelectedAddOn.percentage,
          isNonRatePercentOfTotal ? newlySelectedAddOn.quantity : 0,
          newlySelectedAddOn.rate,
          0,
          newlySelectedAddOn.revenueCostRateType,
          newlySelectedAddOn.externalId,
          { id: currentAddOnId },
          current.load?.id ? { id: current.load?.id } : null,
          isRebill ? newlySelectedAddOn.percentage : null,
          isRebill ? newlySelectedAddOn.rate : null,
          isRebill ? 0 : null,
          null,
        );
        update(index, schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge));
        clearErrors([`addOnCharges.${index}.quantity`, `addOnCharges.${index}.value`]);
      }
    });
  }, [
    watchedAddOnCharges,
    previousWatchedAddOnCharges,
    update,
    clearErrors,
    addOnByAddOnId,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit: () => {
        return new Promise((resolve) => {
          handleSubmit((data) => {
            resolve(data.addOnCharges ?? []);
          })();
        });
      },
      append: handleAppendAddOn,
    }),
    [],
  );

  if (isEditing) {
    return (
      <>
        {fields.map((field, index) => {
          const isDeleted = watchedAddOnCharges?.[index]?._destroy === 1;

          if (isDeleted) {
            return null;
          }

          const editedAddOnCharge = watchedAddOnCharges?.[index];

          // Skip job-associated add-ons (they're rendered in their job sections)
          if (editedAddOnCharge?.jobId) {
            return null;
          }
          const isAddOnSelected = Boolean(editedAddOnCharge?.addOn?.id);
          const isNonRatePercentOfTotal =
            editedAddOnCharge?.addOnRateType === AddOnRateType.RATE_FOR_EACH ||
            editedAddOnCharge?.addOnRateType === AddOnRateType.PER_LOAD ||
            (editedAddOnCharge?.addOnRateType === AddOnRateType.FUEL_SURCHARGE &&
              !editedAddOnCharge?.isPercentage);

          const quantity = editedAddOnCharge?.quantity;
          const rate = editedAddOnCharge?.value;

          const original = originalAddOnChargesById.get(editedAddOnCharge?.id ?? null);
          const dependsOnLineItems = invoiceHelpers.addOnTotalDependsOnLineItems(
            editedAddOnCharge?.addOnRateType as AddOnRateType | null,
          );
          const shouldRecompute =
            dependsOnLineItems ||
            invoiceHelpers.hasAddOnChargeEdits(editedAddOnCharge, original, isRebill);

          const total = isRebill
            ? shouldRecompute
              ? invoiceHelpers.calculateRebillAddOnChargeTotal(
                  editedAddOnCharge?.addOnRateType
                    ? (editedAddOnCharge.addOnRateType as AddOnRateType)
                    : null,
                  editedAddOnCharge?.quantity,
                  editedAddOnCharge?.rebillValue,
                  editedAddOnCharge?.isPercentage ? editedAddOnCharge?.rebillValue : 0,
                  0,
                  0,
                )
              : (original?.rebillTotalAmount ?? 0)
            : shouldRecompute
              ? invoiceHelpers.calculateAddOnChargeTotal(
                  editedAddOnCharge?.addOnRateType
                    ? (editedAddOnCharge.addOnRateType as AddOnRateType)
                    : null,
                  editedAddOnCharge?.quantity,
                  editedAddOnCharge?.value,
                  editedAddOnCharge?.isPercentage ? editedAddOnCharge?.value : 0,
                  0,
                  0,
                )
              : (original?.totalAmount ?? 0);

          return (
            <Box
              key={field.id}
              sx={{
                ...gridConfig,
                borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
              }}
            >
              <AutocompleteFormField
                clearable
                control={control}
                errors={errors}
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                isRequired
                list={addOnSelectOptions}
                name={`addOnCharges[${index}].addOn`}
                sx={{
                  gridColumn: '1 / 4',
                  fontSize: '12px',
                  '& .MuiAutocomplete-inputRoot': { fontSize: '12px', width: '100%' },
                  '& .MuiAutocomplete-endAdornment': {
                    right: '5px !important',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 'fit-content',
                  },
                }}
              />
              {isAddOnSelected && editedAddOnCharge?.addOnRateType && (
                <Typography variant="body2" sx={{ gridColumn: rateTypeColumn }}>
                  {
                    rateTypeTextByAddOnRateType[
                      editedAddOnCharge.addOnRateType as AddOnRateType
                    ]
                  }
                </Typography>
              )}
              {isAddOnSelected && isNonRatePercentOfTotal && (
                <TextFormField
                  control={control}
                  errors={errors}
                  type="number"
                  name={`addOnCharges[${index}].quantity`}
                  sx={{
                    ml: 'auto',
                    '& .MuiFormControl-root': { mb: 0 },
                    '& .MuiOutlinedInput-root': { pr: 0 },
                    '& .MuiOutlinedInput-input': { p: '5px 7px', fontSize: '12px' },
                    gridArea: 'qty',
                    '& .MuiInputBase-input': {
                      fontSize: '12px',
                    },
                  }}
                  inputProps={{ sx: { textAlign: 'right' } }}
                />
              )}
              {isAddOnSelected ? (
                <TextFormField
                  control={control}
                  errors={errors}
                  type="number"
                  name={`addOnCharges[${index}].${isRebill ? 'rebillValue' : 'value'}`}
                  sx={{
                    ml: 'auto',
                    '& .MuiFormControl-root': { mb: 0 },
                    '& .MuiOutlinedInput-root': { pr: 0, pl: '7px' },
                    '& .MuiOutlinedInput-input': {
                      p: '5px 7px 5px 0px',
                      fontSize: '12px',
                    },
                    gridArea: 'rate',
                    '& .MuiInputBase-input': {
                      fontSize: '12px',
                    },
                  }}
                  inputProps={{ sx: { textAlign: 'right' } }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        sx={{
                          mr: '5px',
                          fontSize: '12px',
                        }}
                      >
                        {isNonRatePercentOfTotal ? '$' : '%'}
                      </Typography>
                    ),
                  }}
                />
              ) : null}
              <Box sx={{ gridArea: 'total', fontSize: '12px' }}>
                {quantity && rate && isNonRatePercentOfTotal
                  ? total >= 0
                    ? currencyFormatter(total)
                    : `(${currencyFormatter(-total)})`
                  : '$-'}
              </Box>
              <Tooltip title={t('actions.remove')}>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveAddOnClick(index)}
                  sx={{ gridArea: 'action', justifySelf: 'end' }}
                >
                  <Close
                    fontSize="small"
                    sx={{ color: theme.brandV2.colors.treadGray3 }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </>
    );
  }
  return settlementAddOnCharges.map((addOnCharge) => {
    const isPercentage =
      addOnCharge.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
      (addOnCharge.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
        !!addOnCharge.percentage);

    return (
      <Box
        key={addOnCharge.id}
        sx={{
          ...gridConfig,
          borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
        }}
      >
        <Typography variant="body2" sx={{ gridColumn: '1 / 4' }}>
          {`${t('approvals.driver_pay.settlements.add_on_name_prefixed', { name: addOnCharge.name })}`}
        </Typography>
        <Typography variant="body2" sx={{ gridColumn: rateTypeColumn }}>
          {rateTypeTextByAddOnRateType[addOnCharge.revenueCostRateType]}
        </Typography>
        {!!addOnCharge.quantity && (
          <Typography variant="body2" sx={{ gridArea: 'qty' }}>
            {addOnCharge.quantity}
          </Typography>
        )}

        {isPercentage && (
          <Typography variant="body2" sx={{ gridArea: 'rate' }}>
            {(() => {
              const percentage =
                isRebill && !isNil(addOnCharge.rebillPercentage)
                  ? addOnCharge.rebillPercentage
                  : addOnCharge.percentage;

              return formatPercentage(percentage);
            })()}
          </Typography>
        )}

        {!isPercentage && (
          <Typography variant="body2" sx={{ gridArea: 'rate' }}>
            {(() => {
              const rate =
                isRebill && !isNil(addOnCharge.rebillRevenueRateValue)
                  ? addOnCharge.rebillRevenueRateValue
                  : (addOnCharge.rate ?? 0);

              return rate >= 0
                ? currencyFormatter(rate)
                : `(${currencyFormatter(-rate)})`;
            })()}
          </Typography>
        )}

        <Typography variant="body2" sx={{ gridArea: 'total' }}>
          {(() => {
            const total =
              isRebill && !isNil(addOnCharge.rebillTotalAmount)
                ? addOnCharge.rebillTotalAmount
                : addOnCharge.totalAmount;

            return total >= 0
              ? currencyFormatter(total)
              : `(${currencyFormatter(-total)})`;
          })()}
        </Typography>

        {isEditing && (
          <IconButton
            type="button"
            onClick={() => {}}
            sx={{ gridArea: 'action', justifySelf: 'end' }}
          >
            <CloseIcon fontSize={'small'} />
          </IconButton>
        )}
      </Box>
    );
  });
});

export default observer(SettlementAddOnCharges);
